import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export default class JobQueueService extends Service {
  @tracked jobs = new Map();
  @tracked workers = new Map();
  @tracked jobStats = { completed: 0, failed: 0, queued: 0, running: 0 };

  JOB_TYPES = {
    IMAGE_OPTIMIZATION: 'image_optimization',
    DATA_SYNC: 'data_sync',
    EMAIL_SEND: 'email_send',
    CACHE_WARM: 'cache_warm',
    SEARCH_INDEX: 'search_index',
  };

  constructor() {
    super(...arguments);
    this.initializeWorkers();
  }

  initializeWorkers() {
    // Initialize web workers for heavy processing
    Object.values(this.JOB_TYPES).forEach((jobType) => {
      this.createWorker(jobType);
    });
  }

  createWorker(jobType) {
    try {
      const worker = new Worker(`/workers/${jobType}-worker.js`);
      worker.onmessage = (event) => this.handleWorkerMessage(jobType, event);
      worker.onerror = (error) => this.handleWorkerError(jobType, error);
      this.workers.set(jobType, worker);
    } catch (error) {
      console.warn(`Could not create worker for ${jobType}:`, error.message);
    }
  }

  async enqueueJob(type, data, options = {}) {
    const job = {
      id: this.generateJobId(),
      type,
      data,
      status: 'queued',
      createdAt: Date.now(),
      priority: options.priority || 5,
      retries: 0,
      maxRetries: options.maxRetries || 3,
      timeout: options.timeout || 30000,
    };

    this.jobs.set(job.id, job);
    this.jobStats.queued++;

    // Process job immediately or queue for later
    await this.processJob(job);

    return job.id;
  }

  async processJob(job) {
    job.status = 'running';
    job.startedAt = Date.now();
    this.jobStats.running++;
    this.jobStats.queued--;

    try {
      const result = await this.executeJob(job);
      this.completeJob(job, result);
    } catch (error) {
      this.handleJobError(job, error);
    }
  }

  async executeJob(job) {
    switch (job.type) {
      case this.JOB_TYPES.IMAGE_OPTIMIZATION:
        return await this.optimizeImage(job.data);
      case this.JOB_TYPES.DATA_SYNC:
        return await this.syncData(job.data);
      case this.JOB_TYPES.EMAIL_SEND:
        return await this.sendEmail(job.data);
      case this.JOB_TYPES.CACHE_WARM:
        return await this.warmCache(job.data);
      case this.JOB_TYPES.SEARCH_INDEX:
        return await this.updateSearchIndex(job.data);
      default:
        throw new Error(`Unknown job type: ${job.type}`);
    }
  }

  async optimizeImage(data) {
    // Image optimization logic using canvas or worker
    const { imageUrl, options = {} } = data;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const {
            width = img.width,
            height = img.height,
            quality = 0.8,
          } = options;
          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const optimizedUrl = URL.createObjectURL(blob);
              resolve({ originalUrl: imageUrl, optimizedUrl, size: blob.size });
            },
            'image/jpeg',
            quality,
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = reject;
      img.src = imageUrl;
    });
  }

  async syncData(data) {
    // Data synchronization logic
    const { endpoint, syncType, lastSync } = data;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ syncType, lastSync }),
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status}`);
    }

    return await response.json();
  }

  async sendEmail(data) {
    // Email sending logic (would integrate with email service)
    const { to, subject, body, template } = data;

    // Mock email sending
    await this.delay(1000);

    console.info('Email sent:', { to, subject });
    return { messageId: this.generateJobId(), status: 'sent' };
  }

  async warmCache(data) {
    // Cache warming logic
    const { urls } = data;

    const results = await Promise.allSettled(
      urls.map((url) => fetch(url).then((r) => ({ url, status: r.status }))),
    );

    return {
      total: urls.length,
      successful: results.filter((r) => r.status === 'fulfilled').length,
      failed: results.filter((r) => r.status === 'rejected').length,
    };
  }

  async updateSearchIndex(data) {
    // Search index update logic
    const { documents, operation = 'index' } = data;

    // Mock search indexing
    await this.delay(2000);

    return {
      operation,
      documentsProcessed: documents.length,
      indexedAt: new Date().toISOString(),
    };
  }

  completeJob(job, result) {
    job.status = 'completed';
    job.completedAt = Date.now();
    job.result = result;
    job.duration = job.completedAt - job.startedAt;

    this.jobStats.running--;
    this.jobStats.completed++;

    console.info(`Job ${job.id} completed in ${job.duration}ms`);
  }

  handleJobError(job, error) {
    job.error = error.message;
    job.retries++;

    if (job.retries < job.maxRetries) {
      // Retry with exponential backoff
      const delay = Math.pow(2, job.retries) * 1000;
      setTimeout(() => this.processJob(job), delay);
      console.warn(`Job ${job.id} failed, retrying in ${delay}ms`);
    } else {
      job.status = 'failed';
      job.failedAt = Date.now();
      this.jobStats.running--;
      this.jobStats.failed++;

      console.error(`Job ${job.id} permanently failed:`, error);
    }
  }

  handleWorkerMessage(jobType, event) {
    const { jobId, result, error } = event.data;
    const job = this.jobs.get(jobId);

    if (!job) return;

    if (error) {
      this.handleJobError(job, new Error(error));
    } else {
      this.completeJob(job, result);
    }
  }

  handleWorkerError(jobType, error) {
    console.error(`Worker error for ${jobType}:`, error);
  }

  getJobStatus(jobId) {
    return this.jobs.get(jobId);
  }

  getJobStats() {
    return { ...this.jobStats };
  }

  generateJobId() {
    return `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
